import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompanyProvider } from "@/context/CompanyContext";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Careers, { CareerJob } from "@/pages/Careers";
import InterviewPick from "@/pages/InterviewPick";
import Meet from "@/pages/Meet";
import VideoInterviewRecord from "@/pages/VideoInterviewRecord";
import BureauDashboard from "@/pages/BureauDashboard";
import CompanyDashboard from "@/pages/CompanyDashboard";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import Companies from "@/pages/Companies";
import Employees from "@/pages/Employees";
import PayrollRuns from "@/pages/PayrollRuns";
import Payslips from "@/pages/Payslips";
import Timesheet from "@/pages/Timesheet";
import Scheduling from "@/pages/Scheduling";
import MySchedule from "@/pages/MySchedule";
import Leave from "@/pages/Leave";
import TimeClock from "@/pages/TimeClock";
import KioskKit from "@/pages/KioskKit";
import ChangeRequests from "@/pages/ChangeRequests";
import ChangeRequestForm from "@/pages/ChangeRequestForm";
import ChangeRequestDetail from "@/pages/ChangeRequestDetail";
import Hiring from "@/pages/Hiring";
import OfferLetterEditor from "@/pages/OfferLetterEditor";
import SignOffer from "@/pages/SignOffer";
import DirectDepositForm from "@/pages/DirectDepositForm";
import DirectDeposit from "@/pages/DirectDeposit";
import MyProfile from "@/pages/MyProfile";
import ClientSetup from "@/pages/ClientSetup";
import ClientSetupForm from "@/pages/ClientSetupForm";
import Onboard from "@/pages/Onboard";
import CandidateStatus from "@/pages/CandidateStatus";
import YearEnd from "@/pages/YearEnd";
import Invoices from "@/pages/Invoices";
import ContractorInvoices from "@/pages/ContractorInvoices";
import Billing from "@/pages/Billing";
import CheckPrinting from "@/pages/CheckPrinting";
import Reports from "@/pages/Reports";
import MyReport from "@/pages/MyReport";
import LeaveDecision from "@/pages/LeaveDecision";
import ChangeRequestDecision from "@/pages/ChangeRequestDecision";
import MobilePunch from "@/pages/MobilePunch";
import CheckSetupForm from "@/pages/CheckSetupForm";
import Landing from "@/pages/Landing";
import Policies from "@/pages/Policies";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Leads from "@/pages/Leads";
import AdminSettings from "@/pages/AdminSettings";
import Marketing from "@/pages/Marketing";
import Announcements from "@/pages/Announcements";
import Messages from "@/pages/Messages";
import TaxImpound from "@/pages/TaxImpound";
import Help from "@/pages/Help";
import Developers from "@/pages/Developers";
import ApiAccess from "@/pages/ApiAccess";
import StatePage from "@/pages/StatePage";
import { BlogList, BlogArticle } from "@/pages/Blog";
import Access from "@/pages/Access";
import HRCenter from "@/pages/HRCenter";
import { moduleFor, canView } from "@/lib/access";

function StaffOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (!user || !["bureau_admin", "hr_manager", "processor"].includes(user.role)) return <Navigate to="/login" replace />;
  return children;
}

function FullLoader() {
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

function DashboardRouter() {
  const { user } = useAuth();
  if (user.role === "bureau_admin") return <BureauDashboard />;
  if (user.role === "hr_manager" || user.role === "processor") return <CompanyDashboard />;
  return <EmployeeDashboard />;
}

function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (!canView(user, moduleFor(pathname))) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const isEmployee = user.role === "employee";
  const staff = ["bureau_admin", "hr_manager", "processor"];

  return (
    <CompanyProvider>
      <Layout>
        <Routes>
          <Route path="/dashboard" element={<DashboardRouter />} />
          <Route path="/clients" element={<RoleRoute roles={["bureau_admin"]}><Companies /></RoleRoute>} />
          <Route path="/employees" element={<RoleRoute roles={staff}><Employees /></RoleRoute>} />
          <Route path="/payroll" element={<RoleRoute roles={staff}><PayrollRuns /></RoleRoute>} />
          <Route path="/payslips" element={<Payslips />} />
          <Route path="/timesheet" element={<RoleRoute roles={["employee"]}><Timesheet /></RoleRoute>} />
          <Route path="/my-schedule" element={<RoleRoute roles={["employee"]}><MySchedule /></RoleRoute>} />
          <Route path="/messages" element={<RoleRoute roles={["employee"]}><Messages /></RoleRoute>} />
          <Route path="/announcements" element={<RoleRoute roles={["bureau_admin", "hr_manager"]}><Announcements /></RoleRoute>} />
          <Route path="/scheduling" element={<RoleRoute roles={["bureau_admin", "hr_manager"]}><Scheduling /></RoleRoute>} />
          <Route path="/leave" element={<Leave />} />
          <Route path="/change-requests" element={<RoleRoute roles={staff}><ChangeRequests /></RoleRoute>} />
          <Route path="/change-requests/new" element={<RoleRoute roles={staff}><ChangeRequestForm /></RoleRoute>} />
          <Route path="/change-requests/:id" element={<RoleRoute roles={staff}><ChangeRequestDetail /></RoleRoute>} />
          <Route path="/change-requests/:id/edit" element={<RoleRoute roles={staff}><ChangeRequestForm /></RoleRoute>} />
          <Route path="/hiring" element={<RoleRoute roles={staff}><Hiring /></RoleRoute>} />
          <Route path="/offers/:id" element={<RoleRoute roles={staff}><OfferLetterEditor /></RoleRoute>} />
          <Route path="/direct-deposit" element={<RoleRoute roles={staff}><DirectDeposit /></RoleRoute>} />
          <Route path="/year-end" element={<RoleRoute roles={staff}><YearEnd /></RoleRoute>} />
          <Route path="/invoices" element={<RoleRoute roles={staff}><Invoices /></RoleRoute>} />
          <Route path="/billing" element={<RoleRoute roles={["bureau_admin"]}><Billing /></RoleRoute>} />
          <Route path="/check-printing" element={<RoleRoute roles={["bureau_admin"]}><CheckPrinting /></RoleRoute>} />
          <Route path="/leads" element={<RoleRoute roles={["bureau_admin"]}><Leads /></RoleRoute>} />
          <Route path="/settings" element={<RoleRoute roles={["bureau_admin"]}><AdminSettings /></RoleRoute>} />
          <Route path="/marketing" element={<RoleRoute roles={["bureau_admin"]}><Marketing /></RoleRoute>} />
          <Route path="/tax-impound" element={<RoleRoute roles={staff}><TaxImpound /></RoleRoute>} />
          <Route path="/help" element={<Help />} />
          <Route path="/api-access" element={<RoleRoute roles={["bureau_admin", "hr_manager"]}><ApiAccess /></RoleRoute>} />
          <Route path="/reports" element={<RoleRoute roles={staff}><Reports /></RoleRoute>} />
          <Route path="/my-report" element={<RoleRoute roles={["employee"]}><MyReport /></RoleRoute>} />
          <Route path="/leave/decide/:id" element={<RoleRoute roles={staff}><LeaveDecision /></RoleRoute>} />
          <Route path="/change-requests/:id/decide" element={<RoleRoute roles={staff}><ChangeRequestDecision /></RoleRoute>} />
          <Route path="/punch" element={<RoleRoute roles={["employee"]}><MobilePunch /></RoleRoute>} />
          <Route path="/my-invoices" element={<RoleRoute roles={["employee"]}><ContractorInvoices /></RoleRoute>} />
          <Route path="/me" element={<RoleRoute roles={["employee"]}><MyProfile /></RoleRoute>} />
          <Route path="/client-setup" element={<RoleRoute roles={["bureau_admin"]}><ClientSetup /></RoleRoute>} />
          <Route path="/access" element={<RoleRoute roles={["hr_manager"]}><Access /></RoleRoute>} />
          <Route path="/hr" element={<RoleRoute roles={["bureau_admin", "hr_manager", "processor"]}><HRCenter /></RoleRoute>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
    </CompanyProvider>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/payroll-services/:state" element={<StatePage />} />
            <Route path="/payroll-services/:state/:city" element={<StatePage />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/careers/:slug" element={<Careers />} />
            <Route path="/careers/:slug/:jobId" element={<CareerJob />} />
            <Route path="/interview/:token" element={<InterviewPick />} />
            <Route path="/meet/:code" element={<Meet />} />
            <Route path="/video-interview/:token" element={<VideoInterviewRecord />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/policies" element={<Policies />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/developers" element={<Developers />} />
            <Route path="/timeclock" element={<TimeClock />} />
            <Route path="/timeclock/:companyId" element={<TimeClock />} />
            <Route path="/kiosk-kit/:companyId" element={<StaffOnly><KioskKit /></StaffOnly>} />
            <Route path="/sign/:token" element={<SignOffer />} />
            <Route path="/direct-deposit/:token" element={<DirectDepositForm />} />
            <Route path="/client-setup/:token" element={<ClientSetupForm />} />
            <Route path="/onboard/:token" element={<Onboard />} />
            <Route path="/status/:token" element={<CandidateStatus />} />
            <Route path="/check-setup/:token" element={<CheckSetupForm />} />
            <Route path="/*" element={<AppRoutes />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
